import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { InlineNotification, Loading } from '@carbon/react';
import { formatResource } from 'utils/misc';
import type { CostLineItem } from './types-cost';
import {
  confirmCostAtom,
  costDetailUrl,
  costExtractionRerunUrl,
  createCostDetailResourceAtom,
  editCostAtom,
  editHeldFlagAtom,
  editWorkingInterestAtom,
  removeWellFromCostAtom,
  rerunCostAtom,
  restoreWellFromCostAtom,
  revertCostAtom,
  wellRemovalUrl,
  wellRestorationUrl,
} from './atoms/costs';
import {
  COST_CATEGORIES,
  buildLineItemPayload,
  computeNetAfeCost,
  deriveCostBuckets,
  formatWorkingInterest5,
  lineCost,
  nextKey,
  toDraftCategories,
  toDraftItems,
  type DraftCategory,
  type DraftLineItem,
} from './cost-utils';
import { HOLD_FLAG_OPTIONS } from './identification-utils';
import { CostBucketAccordion } from './cost-bucket-accordion';
import { CostLedgerEditor } from './cost-ledger-editor';
import { StructureCostsHeader } from './structure-costs-header';
import { StructureCostsSummary } from './structure-costs-summary';
import shell from './structuring-module.module.scss';
import styles from './cost.module.scss';

interface Props {
  documentId: number | string;
  /** afeHeaderId — the detail key (GET .../costs/:wellId). */
  wellId: number;
  /** Well display name for the header title (`Well {wellNo} • {wellName}`). */
  wellName?: string | null;
  /** 1-based well position (from the overview row order) for the header title. */
  wellNo?: number;
  onMutated: () => void;
}

export const StructureCosts: FC<Props> = ({
  documentId,
  wellId,
  wellName,
  wellNo,
  onMutated,
}) => {
  const detailHref = costDetailUrl(documentId, wellId);
  const detailAtom = useMemo(
    () => createCostDetailResourceAtom(detailHref),
    [detailHref]
  );
  const [detailResource, reloadDetail] = useAtom(detailAtom);
  const { loading, error, data: detail } = formatResource(detailResource);

  const editCost = useSetAtom(editCostAtom);
  const confirmCost = useSetAtom(confirmCostAtom);
  const revertCost = useSetAtom(revertCostAtom);
  const removeWell = useSetAtom(removeWellFromCostAtom);
  const restoreWell = useSetAtom(restoreWellFromCostAtom);
  const editWorkingInterest = useSetAtom(editWorkingInterestAtom);
  const editHeldFlag = useSetAtom(editHeldFlagAtom);
  const rerunCost = useSetAtom(rerunCostAtom);

  const [draftItems, setDraftItems] = useState<DraftLineItem[]>([]);
  const [draftCategories, setDraftCategories] = useState<DraftCategory[]>([]);
  /** Snapshot captured when entering edit mode so Cancel can restore. */
  const editSnapshotRef = useRef<{
    items: DraftLineItem[];
    categories: DraftCategory[];
  } | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [expandedBuckets, setExpandedBuckets] = useState<Set<string>>(
    new Set()
  );
  const [saving, setSaving] = useState(false);
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [rerunModalOpen, setRerunModalOpen] = useState(false);
  const [rerunning, setRerunning] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // ── Working Interest editor (blur-commit, PATCH /afe_headers/:wellId) ──
  const [wiInput, setWiInput] = useState('');
  const [wiSaving, setWiSaving] = useState(false);

  // ── Hold Flag editor (dropdown-commit, PATCH /afe_headers/:wellId held_flag) ──
  const [heldFlagSaving, setHeldFlagSaving] = useState(false);

  const status = detail?.status ?? 'not_started';
  const isReviewed = status === 'complete';
  const isRemoved = detail?.removed ?? false;
  const canModify = !isReviewed && !isRemoved;

  // Hydrate the local draft whenever the server detail (re)loads.
  const originalItems = useMemo<CostLineItem[]>(
    () => (detail?.categories ?? []).flatMap((c) => c.lineItems),
    [detail]
  );

  useEffect(() => {
    if (!detail) return;
    setDraftItems(toDraftItems(detail.categories));
    setDraftCategories(toDraftCategories(detail.categories));
    setIsEditing(false);
    editSnapshotRef.current = null;
    setWiInput(
      formatWorkingInterest5(detail.summary?.workingInterestPercentage)
    );
  }, [detail]);

  const summary = detail?.summary;
  // Effective WI used for the optimistic Net AFE recompute — prefer the live
  // input while the user is editing, fall back to the server value.
  const effectiveWi = useMemo(() => {
    const trimmed = wiInput.trim();
    if (trimmed === '') return summary?.workingInterestPercentage ?? null;
    const parsed = Number.parseFloat(trimmed);
    return Number.isFinite(parsed)
      ? parsed
      : (summary?.workingInterestPercentage ?? null);
  }, [wiInput, summary]);

  // Optimistic rollups from the live draft (server recomputes authoritatively).
  const activeItems = useMemo(
    () => draftItems.filter((d) => !d.destroyed),
    [draftItems]
  );

  const grandTotal = useMemo(
    () => activeItems.reduce((sum, d) => sum + lineCost(d), 0),
    [activeItems]
  );

  const categoryTotal = (categoryName: string) =>
    activeItems
      .filter((d) => d.category === categoryName)
      .reduce((sum, d) => sum + lineCost(d), 0);

  const contingenciesTotal = categoryTotal('Contingencies & Overhead');
  const otherTotal = categoryTotal('Other');

  const netAfeCost = computeNetAfeCost(grandTotal, effectiveWi);

  const dirty = useMemo(
    () => buildLineItemPayload(draftItems, originalItems).length > 0,
    [draftItems, originalItems]
  );

  const setItem = (key: string, patch: Partial<DraftLineItem>) =>
    setDraftItems((prev) =>
      prev.map((d) => (d.key === key ? { ...d, ...patch } : d))
    );

  // Group the live draft into (category, class) buckets — e.g. "Intangible
  // Drilling" / "Tangible Drilling" render as distinct buckets. Bucket name,
  // shared class-code chip, subtotal (revisedTotal ?? cost*qty) and sort order
  // all come from the shared deriveCostBuckets helper (mirrors the v2 mock).
  const visibleBuckets = useMemo(
    () => deriveCostBuckets(activeItems, lineCost),
    [activeItems]
  );

  const categoryOptions = useMemo(() => {
    const fromDraft = draftCategories.map((c) => c.category);
    return Array.from(new Set([...COST_CATEGORIES, ...fromDraft]));
  }, [draftCategories]);

  // Keep expanded set in sync with the bucket list — newly appearing buckets
  // default open (mirrors v2's bucket-id reconciliation). Returns `prev`
  // untouched when nothing changed so a stable bucket list doesn't churn.
  useEffect(() => {
    setExpandedBuckets((prev) => {
      const ids = new Set(visibleBuckets.map((b) => b.id));
      const next = new Set<string>();
      prev.forEach((id) => {
        if (ids.has(id)) next.add(id);
      });
      visibleBuckets.forEach((b) => {
        if (!next.has(b.id)) next.add(b.id);
      });
      const unchanged =
        next.size === prev.size && [...next].every((n) => prev.has(n));
      return unchanged ? prev : next;
    });
  }, [visibleBuckets]);

  const toggleBucket = (bucketId: string) =>
    setExpandedBuckets((prev) => {
      const next = new Set(prev);
      if (next.has(bucketId)) next.delete(bucketId);
      else next.add(bucketId);
      return next;
    });

  const allExpanded =
    visibleBuckets.length > 0 && expandedBuckets.size >= visibleBuckets.length;

  const toggleAll = () => {
    if (allExpanded) setExpandedBuckets(new Set());
    else setExpandedBuckets(new Set(visibleBuckets.map((b) => b.id)));
  };

  // ── Edit-mode lifecycle (snapshot + Cancel/Save) ──

  const enterEditMode = () => {
    editSnapshotRef.current = {
      items: draftItems.map((d) => ({ ...d })),
      categories: draftCategories.map((c) => ({ ...c })),
    };
    setIsEditing(true);
  };

  const cancelEdit = () => {
    if (editSnapshotRef.current) {
      setDraftItems(editSnapshotRef.current.items);
      setDraftCategories(editSnapshotRef.current.categories);
    }
    editSnapshotRef.current = null;
    setIsEditing(false);
  };

  const handleAddLineItem = (category: string) => {
    const key = nextKey();
    setDraftItems((prev) => [
      ...prev,
      {
        key,
        id: null,
        code: null,
        description: '',
        costClass: 'intangible',
        category,
        subCategory: null,
        classCode: null,
        costPerUnit: 0,
        quantity: 1,
        revisedTotal: null,
        systemGenerated: false,
        isNew: true,
        destroyed: false,
      },
    ]);
  };

  const handleDeleteLineItem = (item: DraftLineItem) => {
    if (item.isNew) {
      setDraftItems((prev) => prev.filter((d) => d.key !== item.key));
    } else {
      setItem(item.key, { destroyed: true });
    }
  };

  const handleSave = () => {
    const payload = buildLineItemPayload(draftItems, originalItems);
    if (payload.length === 0) {
      // No BE-backed changes — just leave edit mode (e.g. only code/classCode
      // touched, which don't persist yet).
      editSnapshotRef.current = null;
      setIsEditing(false);
      return;
    }
    setSaving(true);
    setActionError(null);
    editCost({
      url: detailHref,
      method: 'PATCH',
      data: { cost: { line_items: payload } },
      onSuccess: () => {
        setSaving(false);
        editSnapshotRef.current = null;
        setIsEditing(false);
        reloadDetail();
        onMutated();
      },
      onError: (m) => {
        setSaving(false);
        setActionError(m ?? 'Failed to save cost changes.');
      },
    });
  };

  const handleRerunExtraction = () => {
    if (rerunning) return;
    setRerunning(true);
    setActionError(null);
    rerunCost({
      url: costExtractionRerunUrl(documentId, wellId),
      method: 'POST',
      onSuccess: () => {
        setRerunning(false);
        setRerunModalOpen(false);
        reloadDetail();
        onMutated();
      },
      onError: (m) => {
        setRerunning(false);
        setRerunModalOpen(false);
        setActionError(m ?? 'Failed to rerun cost extraction.');
      },
    });
  };

  const handleMarkCorrect = () => {
    const href = detail?.resources?.confirmation?.href;
    if (!href) return;
    setActionError(null);
    confirmCost({
      url: href,
      method: 'POST',
      onSuccess: () => {
        reloadDetail();
        onMutated();
      },
      onError: (m) => setActionError(m ?? 'Failed to confirm costs.'),
    });
  };

  const handleRemoveCorrect = () => {
    const href = detail?.resources?.confirmation?.href;
    if (!href) return;
    setActionError(null);
    revertCost({
      url: href,
      method: 'DELETE',
      onSuccess: () => {
        reloadDetail();
        onMutated();
      },
      onError: (m) => setActionError(m ?? 'Failed to remove correct status.'),
    });
  };

  const confirmRemoveWell = () => {
    const afeTargetId = detail?.afeTargetId;
    if (afeTargetId === null || afeTargetId === undefined) return;
    setActionError(null);
    removeWell({
      url: wellRemovalUrl(documentId, afeTargetId),
      method: 'DELETE',
      onSuccess: () => {
        setRemoveModalOpen(false);
        reloadDetail();
        onMutated();
      },
      onError: (m) => {
        setRemoveModalOpen(false);
        setActionError(m ?? 'Failed to remove well.');
      },
    });
  };

  const handleUndoRemove = () => {
    const afeTargetId = detail?.afeTargetId;
    if (afeTargetId === null || afeTargetId === undefined) return;
    setActionError(null);
    restoreWell({
      url: wellRestorationUrl(documentId, afeTargetId),
      method: 'POST',
      onSuccess: () => {
        reloadDetail();
        onMutated();
      },
      onError: (m) => setActionError(m ?? 'Failed to restore well.'),
    });
  };

  const commitWorkingInterest = () => {
    const trimmed = wiInput.trim();
    const parsed = trimmed === '' ? null : Number.parseFloat(trimmed);
    if (trimmed !== '' && !Number.isFinite(parsed)) {
      // Invalid input — restore the last server value.
      setWiInput(formatWorkingInterest5(summary?.workingInterestPercentage));
      return;
    }
    const next = parsed as number | null;
    if (next === (summary?.workingInterestPercentage ?? null)) return; // no change
    setWiSaving(true);
    setActionError(null);
    editWorkingInterest({
      afeHeaderId: wellId,
      workingInterestPercentage: next,
      onSuccess: () => {
        setWiSaving(false);
        reloadDetail();
        onMutated();
      },
      onError: (m) => {
        setWiSaving(false);
        setActionError(m ?? 'Failed to update working interest.');
      },
    });
  };

  // Held Flag commits immediately on select. The dropdown stores the raw enum
  // value ('held' / 'submittal'); the "None" option ('') clears the column
  // (PATCH held_flag: null), since the AfeHeader enum rejects an empty string.
  const selectedHeldFlagOption =
    HOLD_FLAG_OPTIONS.find((o) => o.value === (summary?.heldFlag ?? '')) ??
    HOLD_FLAG_OPTIONS[0];

  const commitHeldFlag = (option: { label: string; value: string } | null) => {
    if (!option) return;
    const next = option.value === '' ? null : option.value;
    if (next === (summary?.heldFlag ?? null)) return; // no change
    setHeldFlagSaving(true);
    setActionError(null);
    editHeldFlag({
      afeHeaderId: wellId,
      heldFlag: next,
      onSuccess: () => {
        setHeldFlagSaving(false);
        reloadDetail();
        onMutated();
      },
      onError: (m) => {
        setHeldFlagSaving(false);
        setActionError(m ?? 'Failed to update held flag.');
      },
    });
  };

  return (
    <div className={shell.matchReview}>
      <StructureCostsHeader
        wellId={wellId}
        wellName={wellName}
        wellNo={wellNo}
        status={status}
        isEditing={isEditing}
        isRemoved={isRemoved}
        isReviewed={isReviewed}
        canModify={canModify}
        dirty={dirty}
        saving={saving}
        rerunning={rerunning}
        allExpanded={allExpanded}
        hasBuckets={visibleBuckets.length > 0}
        afeTargetId={detail?.afeTargetId}
        removeModalOpen={removeModalOpen}
        rerunModalOpen={rerunModalOpen}
        defaultAddCategory={categoryOptions[0] ?? 'Drilling'}
        onAddLineItem={handleAddLineItem}
        onCancelEdit={cancelEdit}
        onSave={handleSave}
        onUndoRemove={handleUndoRemove}
        onRemoveCorrect={handleRemoveCorrect}
        onToggleAll={toggleAll}
        onMarkCorrect={handleMarkCorrect}
        onEnterEditMode={enterEditMode}
        onOpenRemoveModal={() => setRemoveModalOpen(true)}
        onCloseRemoveModal={() => setRemoveModalOpen(false)}
        onConfirmRemoveWell={confirmRemoveWell}
        onOpenRerunModal={() => setRerunModalOpen(true)}
        onCloseRerunModal={() => setRerunModalOpen(false)}
        onRerunExtraction={handleRerunExtraction}
      />

      <p className={styles.sectionLabel}>Cost Breakdown</p>
      <p className={styles.sectionDesc}>
        {isRemoved
          ? 'This well has been removed. The cost breakdown below is no longer applicable.'
          : isReviewed
            ? 'This cost breakdown has been confirmed. You can review line items in context.'
            : 'Review the extracted cost line items for this well. Edit values, class, category and sub-category, then Save and Mark as Correct once verified.'}
      </p>

      {actionError && (
        <InlineNotification
          kind="error"
          lowContrast
          title="Error"
          subtitle={actionError}
          onCloseButtonClick={() => setActionError(null)}
        />
      )}
      {error && (
        <InlineNotification
          kind="error"
          lowContrast
          title="Unable to load cost breakdown"
          subtitle={error}
        />
      )}

      {loading && !detail ? (
        <Loading withOverlay={false} small description="Loading costs…" />
      ) : (
        <>
          <StructureCostsSummary
            wellId={wellId}
            grandTotal={grandTotal}
            drillingTotal={categoryTotal('Drilling')}
            completionTotal={categoryTotal('Completion')}
            facilitiesTotal={categoryTotal('Facilities')}
            contingenciesTotal={contingenciesTotal}
            otherTotal={otherTotal}
            netAfeCost={netAfeCost}
            canModify={canModify}
            wiInput={wiInput}
            wiSaving={wiSaving}
            onWiChange={setWiInput}
            onWiCommit={commitWorkingInterest}
            selectedHeldFlagOption={selectedHeldFlagOption}
            heldFlagSaving={heldFlagSaving}
            onHeldFlagChange={commitHeldFlag}
          />

          <div className={styles.groups}>
            {isEditing ? (
              <CostLedgerEditor
                // Pass the full draft (incl. items marked destroyed) so a removed
                // line item stays visible — struck through with an Undo — instead
                // of vanishing. Cost rollups still use `activeItems` (destroyed
                // excluded); the destroy is persisted via `_destroy` only on Save.
                items={draftItems}
                categoryOptions={categoryOptions}
                setItem={setItem}
                onAddLineItem={handleAddLineItem}
                onDeleteLineItem={handleDeleteLineItem}
              />
            ) : (
              <CostBucketAccordion
                buckets={visibleBuckets}
                expandedBuckets={expandedBuckets}
                onToggleBucket={toggleBucket}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};
